import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { monoFontSx } from "../ThemeContext";

const computeReminderStatus = (reminder, currentMileage) => {
  if (!reminder) return "Upcoming";
  if (reminder.status?.toLowerCase() === "completed") return "Completed";

  const today = new Date();
  const dueDate = reminder.dueDate ? new Date(reminder.dueDate) : null;
  const dueMileage = Number(reminder.dueMileage);

  const overdueByDate = dueDate ? dueDate < today : false;
  const overdueByMileage = Number.isFinite(dueMileage) && currentMileage != null ? dueMileage <= currentMileage : false;

  if (overdueByDate || overdueByMileage) {
    return "Overdue";
  }

  const dueSoonByDate = dueDate
    ? (dueDate - today) / (1000 * 60 * 60 * 24) <= 14
    : false;
  const dueSoonByMileage = Number.isFinite(dueMileage) && currentMileage != null ? dueMileage <= currentMileage + 500 : false;

  if (dueSoonByDate || dueSoonByMileage) {
    return "Due Soon";
  }

  return "Upcoming";
};

const groupReminders = (reminders, currentMileage) => {
  return reminders.reduce(
    (acc, reminder) => {
      const status = computeReminderStatus(reminder, currentMileage);
      acc[status].push(reminder);
      return acc;
    },
    {
      Overdue: [],
      "Due Soon": [],
      Upcoming: [],
      Completed: [],
    }
  );
};

const formatReminderSubtitle = (reminder) => {
  const parts = [];
  if (reminder.dueDate) parts.push(`Due ${reminder.dueDate}`);
  if (reminder.dueMileage) parts.push(`at ${reminder.dueMileage} miles`);
  return parts.join(" • ");
};

const RemindersSection = ({
  carList,
  selectedCar,
  reminders,
  currentMileage,
  onCreateReminder,
  onUpdateReminder,
  onDeleteReminder,
}) => {
  const [formState, setFormState] = useState({
    title: "",
    dueDate: "",
    dueMileage: "",
    note: "",
    vehicleId: selectedCar?.id || "",
  });

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      vehicleId: selectedCar?.id || "",
    }));
  }, [selectedCar]);

  const visibleReminders = useMemo(() => {
    if (!selectedCar) return reminders;
    return reminders.filter((reminder) => reminder.vehicleId === selectedCar.id);
  }, [reminders, selectedCar]);

  const grouped = useMemo(
    () => groupReminders(visibleReminders, currentMileage),
    [visibleReminders, currentMileage]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!onCreateReminder) return;

    const trimmedTitle = formState.title.trim();
    if (!trimmedTitle) return;

    await onCreateReminder({
      title: trimmedTitle,
      dueDate: formState.dueDate || null,
      dueMileage: formState.dueMileage ? Number(formState.dueMileage) : null,
      note: formState.note || null,
      vehicleId: formState.vehicleId || null,
    });

    setFormState({
      title: "",
      dueDate: "",
      dueMileage: "",
      note: "",
      vehicleId: selectedCar?.id || "",
    });
  };

  const renderGroup = (title, items) => (
    <Box sx={{ mt: 3.5 }}>
      <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: 0.8 }}>
        {title}
      </Typography>
      <Divider sx={{ mt: 0.5, mb: items.length ? 0 : 1 }} />
      {items.length ? (
        items.map((reminder, i) => (
          <Box
            key={reminder.id}
            sx={{
              py: 1.75,
              borderBottom: i === items.length - 1 ? "none" : "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700 }}>{reminder.title}</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                {reminder.status === "completed" ? "Completed" : title}
              </Typography>
            </Box>
            {reminder.dueDate || reminder.dueMileage ? (
              <Typography sx={{ color: "text.secondary", mb: 0.75, fontSize: 13, ...monoFontSx }}>
                {formatReminderSubtitle(reminder)}
              </Typography>
            ) : null}
            {reminder.note ? (
              <Typography sx={{ color: "text.secondary", mb: 1, whiteSpace: "pre-wrap" }}>
                {reminder.note}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {reminder.status !== "completed" ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onUpdateReminder?.({ ...reminder, status: "completed" })}
                >
                  Mark Completed
                </Button>
              ) : null}
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => onDeleteReminder?.(reminder.id)}
              >
                Delete
              </Button>
            </Stack>
          </Box>
        ))
      ) : (
        <Typography sx={{ color: "text.secondary", py: 1.5 }}>No reminders in this section.</Typography>
      )}
    </Box>
  );

  return (
    <Box>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
        Reminders
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Track upcoming maintenance reminders for your selected vehicle. Save reminders once and access them across sessions.
      </Typography>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        Add Reminder
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          label="Title"
          value={formState.title}
          onChange={(e) => setFormState({ ...formState, title: e.target.value })}
          required
          fullWidth
          margin="normal"
        />
        <TextField
          label="Due Date"
          type="date"
          value={formState.dueDate}
          onChange={(e) => setFormState({ ...formState, dueDate: e.target.value })}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Due Mileage"
          type="number"
          value={formState.dueMileage}
          onChange={(e) => setFormState({ ...formState, dueMileage: e.target.value })}
          fullWidth
          margin="normal"
          inputProps={{ min: 0 }}
        />
        <TextField
          label="Notes"
          value={formState.note}
          onChange={(e) => setFormState({ ...formState, note: e.target.value })}
          fullWidth
          multiline
          rows={3}
          margin="normal"
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Vehicle</InputLabel>
          <Select
            value={formState.vehicleId || ""}
            label="Vehicle"
            onChange={(e) => setFormState({ ...formState, vehicleId: e.target.value })}
          >
            {selectedCar ? (
              <MenuItem value={selectedCar.id}>
                {selectedCar.nickname
                  ? `${selectedCar.nickname} (${selectedCar.year} ${selectedCar.make} ${selectedCar.model})`
                  : `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}`}
              </MenuItem>
            ) : null}
            {carList
              .filter((car) => !selectedCar || car.id !== selectedCar.id)
              .map((car) => (
                <MenuItem key={car.id} value={car.id}>
                  {car.nickname
                    ? `${car.nickname} (${car.year} ${car.make} ${car.model})`
                    : `${car.year} ${car.make} ${car.model}`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Button sx={{ mt: 1.5 }} variant="contained" type="submit">
          Save Reminder
        </Button>
      </Box>

      {renderGroup("Overdue", grouped.Overdue)}
      {renderGroup("Due Soon", grouped["Due Soon"])}
      {renderGroup("Upcoming", grouped.Upcoming)}
      {renderGroup("Completed", grouped.Completed)}
    </Box>
  );
};

export default RemindersSection;
